import { useSnackbar } from 'notistack';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';

import { useBoolean } from 'src/hooks/use-boolean';

import {
  useGetStoreIntegrationsQuery,
  useGetStoreModulePermissionsQuery,
  useSetYampiStoreIntegrationMutation,
} from 'src/graphql/generated';

import FormProvider from 'src/components/hook-form/form-provider';
import { PremiumFeatureDialog } from 'src/components/premium-feature-dialog/premium-feature-dialog';

import { TCardIntegration } from './types/card-integration';
import { IntegrationCard } from './utils/card-intergration';
import { CardIntegration } from './components/card-integration';
import { ConfigTrayModal } from './components/config-tray-modal';
import { ConfigOmieModal } from './components/config-omie-modal';
import { ConfigBlingModal } from './components/config-bling-modal';
import { ConfigDapicModal } from './components/config-dapic-modal';
import { ConfigYampiModal } from './components/config-yampi-modal';
import { ConfigTrinksModal } from './components/config-trinks-modal';
import { ConfigShopifyModal } from './components/config-shopify-modal';
import { ConfigWhatsappModal } from './components/config-whatsapp-modal';
import { ConfigHostgatorModal } from './components/config-hostgator-modal';
import { ConfigNuvemshopModal } from './components/config-nuvemshop-modal';
import { ConfigSMSInfobipModal } from './components/config-smsinfobip-modal';
import { EmailProviderModal } from './components/config-email-provider-modal';
import { ConfigZekiMessageModal } from './components/config-zekiMessage-modal';
import ConfigIntegrationsToolbar from './components/config-integrations-toolbar';
import { ConfigLojaIntegradaModal } from './components/config-loja-integrada-modal';
import { canOpenNewIntegration, type IntegrationConnectedFlags } from './utils/integration-quota';
import { ConfigHelenaModal, GET_HELENA_STORE_INTEGRATION } from './components/config-helena-modal';
import {
  IntegrationFormSchema,
  integrationFormSchemaDefaultValues,
} from './schema/integration-filter-form-schema';

// ----------------------------------------------------------------------

enum Integrations {
  WHATSAPP = 'WHATSAPP',
  HELENA = 'HELENA',
  TRAY = 'TRAY',
  ZAPI = 'ZAPI',
  OMIE = 'OMIE',
  BLING = 'BLING',
  SMS = 'SMS',
  DAPIC = 'DAPIC',
  EMAIL = 'EMAIL',
  HOSTGATOR = 'HOSTGATOR',
  EVOLUTION_API = 'EVOLUTION_API',
  NUVEMSHOP = 'NUVEMSHOP',
  SHOPIFY = 'SHOPIFY',
  LOJA_INTEGRADA = 'LOJA_INTEGRADA',
  QUICK_ERP = 'Quick ERP',
  YAMPI = 'YAMPI',
  TRINKS = 'TRINKS',
}

// ----------------------------------------------------------------------

export default function ConfigIntegrationsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredCards, setFilteredCards] = useState<TCardIntegration[] | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(IntegrationFormSchema),
    defaultValues: integrationFormSchemaDefaultValues,
  });

  const watched = useWatch({ control: methods.control });

  const whatsappModal = useBoolean();
  const helenaModal = useBoolean();
  const trayModal = useBoolean();
  const zekiMessageModal = useBoolean();
  const omieModal = useBoolean();
  const blingModal = useBoolean();
  const smsInfobipModal = useBoolean();
  const dapicModal = useBoolean();
  const emailProviderModal = useBoolean();
  const hostgatorModal = useBoolean();
  const nuvemshopModal = useBoolean();
  const shopifyModal = useBoolean();
  const lojaIntegradaModal = useBoolean();
  const yampiModal = useBoolean();
  const trinksModal = useBoolean();
  const premiumFeatureDialog = useBoolean();
  const showPremiumFeatureDialog = premiumFeatureDialog.onTrue;

  const [setYampiIntegration] = useSetYampiStoreIntegrationMutation();

  const { data: integrationData, refetch } = useGetStoreIntegrationsQuery();
  const { data: helenaIntegrationData, refetch: refetchHelenaIntegration } = useQuery(
    GET_HELENA_STORE_INTEGRATION
  );
  const { data: modulePermissionsData, loading: modulePermissionsLoading } =
    useGetStoreModulePermissionsQuery();
  const modulePermissions = modulePermissionsData?.getStoreModulePermissions;

  const zekiMessage = integrationData?.infoIntegration?.zekiMessage;

  const integratedTray = Boolean(integrationData?.infoIntegration?.traystoreintegration?.connected);
  const integratedOmie = Boolean(integrationData?.infoIntegration?.omiestoreintegration?.activated);
  const isEvolution = !zekiMessage || zekiMessage === 'EVOLUTION';
  const integratedZekiMessage = Boolean(
    integrationData?.infoIntegration?.evolutionapiintegration?.connected ||
      integrationData?.infoIntegration?.zapiintegration?.connected
  );
  const integratedSMSInfobip = Boolean(
    integrationData?.infoIntegration?.smsinfobipintegration?.activated
  );
  const integratedBling = Boolean(
    integrationData?.infoIntegration?.blingstoreintegration?.connected
  );
  const integratedDapic = Boolean(
    integrationData?.infoIntegration?.webpicstoreintegration?.connected
  );
  const integratedEmailProvider = Boolean(
    integrationData?.infoIntegration?.emailproviderstoreintegration?.isActive
  );
  const integratedWhatsapp = Boolean(integrationData?.infoIntegration?.whatsappconfig?.activated);
  const currentDataHelena = helenaIntegrationData?.getHelenaStoreIntegration;
  const integratedHelena = Boolean(currentDataHelena?.activated && currentDataHelena?.connected);
  const integratedHostgator = Boolean(
    integrationData?.infoIntegration?.hostgatorstoreintegration?.connected
  );

  const integratedShopify = Boolean(
    integrationData?.infoIntegration?.shopifystoreintegration?.activated
  );

  const integratedNuvemshop = Boolean(
    integrationData?.infoIntegration?.nuvemshopstoreintegration?.connected
  );
  const integratedLojaIntegrada = Boolean(
    integrationData?.infoIntegration?.lojaintegradastoreintegration?.connected
  );
  const integratedYampi = Boolean(
    (integrationData?.infoIntegration as any)?.yampistoreintegration?.connected
  );
  const integratedTrinks = Boolean(
    integrationData?.infoIntegration?.trinksstoreintegration?.connected
  );

  const currentDataTray = integrationData?.infoIntegration?.traystoreintegration;
  const currentDataWhatsapp = integrationData?.infoIntegration?.whatsappconfig;
  const currentDataOmie = integrationData?.infoIntegration?.omiestoreintegration;
  const currentDataZapi = integrationData?.infoIntegration?.zapiintegration;
  const currentDataEvolutionApi = integrationData?.infoIntegration?.evolutionapiintegration;
  const currentDataSMSInfobip = integrationData?.infoIntegration?.smsinfobipintegration;
  const currentDataBling = integrationData?.infoIntegration?.blingstoreintegration;
  const currentDataDapic = integrationData?.infoIntegration?.webpicstoreintegration;
  const currentDataEmailProvider = integrationData?.infoIntegration?.emailproviderstoreintegration;
  const currentDataHostgator = integrationData?.infoIntegration?.hostgatorstoreintegration;
  const currentDataNuvemshop = integrationData?.infoIntegration?.nuvemshopstoreintegration;
  const currentDataShopify = integrationData?.infoIntegration?.shopifystoreintegration;
  const currentDataLojaIntegrada = integrationData?.infoIntegration?.lojaintegradastoreintegration;
  const currentDataYampi = (integrationData?.infoIntegration as any)?.yampistoreintegration;
  const currentDataTrinks = integrationData?.infoIntegration?.trinksstoreintegration;

  const refetchCommunicationIntegrations = useCallback(async () => {
    await Promise.all([refetch(), refetchHelenaIntegration()]);
  }, [refetch, refetchHelenaIntegration]);

  const connectedFlags: IntegrationConnectedFlags = useMemo(
    () => ({
      integratedTray,
      integratedOmie,
      integratedZekiMessage,
      integratedSMSInfobip,
      integratedBling,
      integratedDapic,
      integratedEmailProvider,
      integratedWhatsapp,
      integratedHelena,
      integratedHostgator,
      integratedShopify,
      integratedNuvemshop,
      integratedLojaIntegrada,
      integratedYampi,
      integratedTrinks,
    }),
    [
      integratedTray,
      integratedOmie,
      integratedZekiMessage,
      integratedSMSInfobip,
      integratedBling,
      integratedDapic,
      integratedEmailProvider,
      integratedWhatsapp,
      integratedHelena,
      integratedHostgator,
      integratedShopify,
      integratedNuvemshop,
      integratedLojaIntegrada,
      integratedYampi,
      integratedTrinks,
    ]
  );

  const openWithQuotaCheck = useCallback(
    (cardType: IntegrationCard, hasData: boolean, openModal: () => void) => {
      if (!canOpenNewIntegration(cardType, hasData, modulePermissions, connectedFlags)) {
        showPremiumFeatureDialog();
        return;
      }
      openModal();
    },
    [modulePermissions, connectedFlags, showPremiumFeatureDialog]
  );

  const allDataCardIntegrations: TCardIntegration[] = useMemo(
    () => [
      {
        logoURL: '/assets/images/tray-logo.png',
        heading: 'Tray',
        subheading: 'Integração com a conta da tray',
        isConnected: integratedTray,
        hasData: Boolean(currentDataTray),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.E_COMMERCES,
            Boolean(currentDataTray),
            trayModal.onTrue
          ),
        type: IntegrationCard.E_COMMERCES,
      },
      {
        logoURL: '/assets/images/omie-logo.png',
        heading: 'Omie',
        subheading: 'Integração com a conta da Omie',
        isConnected: integratedOmie,
        hasData: Boolean(currentDataOmie),
        onAction: () =>
          openWithQuotaCheck(IntegrationCard.ERP, Boolean(currentDataOmie), omieModal.onTrue),
        type: IntegrationCard.ERP,
      },
      {
        logoURL: '/assets/images/bling-logo.png',
        heading: 'Bling',
        subheading: 'Integração com o a conta da Bling',
        isConnected: integratedBling,
        hasData: Boolean(currentDataBling),
        onAction: () =>
          openWithQuotaCheck(IntegrationCard.ERP, Boolean(currentDataBling), blingModal.onTrue),
        type: IntegrationCard.ERP,
      },
      {
        logoURL: '/assets/images/whatsapp-logo.png',
        heading: 'API Oficial do Meta',
        subheading: 'Integração com a API Oficial do WhatsApp Meta',
        isConnected: integratedWhatsapp,
        hasData: Boolean(currentDataWhatsapp),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.COMMUNICATION,
            Boolean(currentDataWhatsapp),
            whatsappModal.onTrue
          ),
        type: IntegrationCard.COMMUNICATION,
      },
      {
        logoURL: '/logo/logo_single.svg',
        heading: 'ZEKI CRM',
        subheading: 'Conecte seu canal oficial para disparos por templates',
        isConnected: integratedHelena,
        hasData: Boolean(currentDataHelena),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.COMMUNICATION,
            Boolean(currentDataHelena),
            helenaModal.onTrue
          ),
        type: IntegrationCard.COMMUNICATION,
      },
      {
        logoURL: '/assets/ZekiMessage.png',
        heading: 'ZekiMessage',
        subheading: 'Conecte seu WhatsApp com a nossa Solução de Mensagens',
        isConnected: integratedZekiMessage,
        hasData: isEvolution ? Boolean(currentDataEvolutionApi) : Boolean(currentDataZapi),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.COMMUNICATION,
            isEvolution ? Boolean(currentDataEvolutionApi) : Boolean(currentDataZapi),
            zekiMessageModal.onTrue
          ),
        type: IntegrationCard.COMMUNICATION,
      },
      {
        logoURL: '/assets/images/sms-logo.png',
        heading: 'Infobip',
        subheading: 'Integração com o serviço da Infobip',
        isConnected: integratedSMSInfobip,
        hasData: Boolean(currentDataSMSInfobip),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.COMMUNICATION,
            Boolean(currentDataSMSInfobip),
            smsInfobipModal.onTrue
          ),
        type: IntegrationCard.COMMUNICATION,
      },
      {
        logoURL: '/assets/images/dapic-logo.png',
        heading: 'Dapic',
        subheading: 'Integração com a conta Dapic - WebPic',
        isConnected: integratedDapic,
        hasData: Boolean(currentDataDapic),
        onAction: () =>
          openWithQuotaCheck(IntegrationCard.ERP, Boolean(currentDataDapic), dapicModal.onTrue),
        type: IntegrationCard.ERP,
      },
      {
        logoURL: '/assets/images/email-logo.png',
        heading: 'E-mail',
        subheading: 'Integração com o serviço de E-mail',
        isConnected: integratedEmailProvider,
        hasData: Boolean(currentDataEmailProvider),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.COMMUNICATION,
            Boolean(currentDataEmailProvider),
            emailProviderModal.onTrue
          ),
        type: IntegrationCard.COMMUNICATION,
      },
      {
        logoURL: '/assets/images/icon-hostgator.png',
        heading: 'Loja HostGator',
        subheading: 'Integração com a Loja da HostGator',
        isConnected: integratedHostgator,
        hasData: Boolean(currentDataHostgator),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.E_COMMERCES,
            Boolean(currentDataHostgator),
            hostgatorModal.onTrue
          ),
        type: IntegrationCard.E_COMMERCES,
      },
      {
        logoURL: '/assets/images/nuvemshop-logo.png',
        heading: 'Loja Nuvemshop',
        subheading: 'Integração com a Loja da Nuvemshop',
        isConnected: integratedNuvemshop,
        hasData: Boolean(currentDataNuvemshop),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.E_COMMERCES,
            Boolean(currentDataNuvemshop),
            nuvemshopModal.onTrue
          ),
        type: IntegrationCard.E_COMMERCES,
      },
      {
        logoURL: '/assets/images/shopify-logo.png',
        heading: 'Shopify',
        subheading: 'Integração com a Shopify',
        isConnected: integratedShopify,
        hasData: Boolean(currentDataShopify),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.E_COMMERCES,
            Boolean(currentDataShopify),
            shopifyModal.onTrue
          ),
        type: IntegrationCard.E_COMMERCES,
      },
      {
        logoURL: '/assets/images/loja_integrada_logo.png',
        heading: 'Loja Integrada',
        subheading: 'Integração com a Loja Integrada',
        isConnected: integratedLojaIntegrada,
        hasData: Boolean(currentDataLojaIntegrada),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.E_COMMERCES,
            Boolean(currentDataLojaIntegrada),
            lojaIntegradaModal.onTrue
          ),
        type: IntegrationCard.E_COMMERCES,
      },
      {
        logoURL: '/assets/images/yampi-logo.svg',
        heading: 'Yampi',
        subheading: 'Integração com a loja Yampi',
        isConnected: integratedYampi,
        hasData: Boolean(currentDataYampi),
        onAction: () =>
          openWithQuotaCheck(
            IntegrationCard.E_COMMERCES,
            Boolean(currentDataYampi),
            yampiModal.onTrue
          ),
        type: IntegrationCard.E_COMMERCES,
      },
      {
        logoURL: '/assets/images/trinks-logo.png',
        heading: 'Trinks',
        subheading: 'Integração com a conta da Trinks',
        isConnected: integratedTrinks,
        hasData: Boolean(currentDataTrinks),
        onAction: () =>
          openWithQuotaCheck(IntegrationCard.ERP, Boolean(currentDataTrinks), trinksModal.onTrue),
        type: IntegrationCard.ERP,
      },
    ],
    [
      openWithQuotaCheck,
      integratedTray,
      integratedOmie,
      integratedBling,
      integratedWhatsapp,
      integratedHelena,
      integratedZekiMessage,
      integratedSMSInfobip,
      integratedDapic,
      integratedEmailProvider,
      integratedHostgator,
      integratedNuvemshop,
      integratedShopify,
      integratedLojaIntegrada,
      integratedYampi,
      integratedTrinks,
      currentDataTray,
      currentDataOmie,
      currentDataBling,
      currentDataWhatsapp,
      currentDataHelena,
      currentDataEvolutionApi,
      currentDataZapi,
      currentDataSMSInfobip,
      currentDataDapic,
      currentDataEmailProvider,
      currentDataHostgator,
      currentDataNuvemshop,
      currentDataShopify,
      currentDataLojaIntegrada,
      currentDataYampi,
      currentDataTrinks,
      isEvolution,
      trayModal.onTrue,
      omieModal.onTrue,
      blingModal.onTrue,
      whatsappModal.onTrue,
      helenaModal.onTrue,
      zekiMessageModal.onTrue,
      smsInfobipModal.onTrue,
      dapicModal.onTrue,
      emailProviderModal.onTrue,
      hostgatorModal.onTrue,
      nuvemshopModal.onTrue,
      shopifyModal.onTrue,
      lojaIntegradaModal.onTrue,
      yampiModal.onTrue,
      trinksModal.onTrue,
    ]
  );

  const handleCloseModal = (modalName: Integrations) => {
    switch (modalName) {
      case Integrations.TRAY:
        trayModal.onFalse();
        break;
      case Integrations.OMIE:
        omieModal.onFalse();
        break;
      case Integrations.WHATSAPP:
        whatsappModal.onFalse();
        break;
      case Integrations.HELENA:
        helenaModal.onFalse();
        break;
      case Integrations.ZAPI:
      case Integrations.EVOLUTION_API:
        zekiMessageModal.onFalse();
        break;
      case Integrations.SMS:
        smsInfobipModal.onFalse();
        break;
      case Integrations.BLING:
        blingModal.onFalse();
        break;
      case Integrations.DAPIC:
        dapicModal.onFalse();
        break;
      case Integrations.EMAIL:
        emailProviderModal.onFalse();
        break;
      case Integrations.HOSTGATOR:
        hostgatorModal.onFalse();
        break;
      case Integrations.NUVEMSHOP:
        nuvemshopModal.onFalse();
        break;
      case Integrations.SHOPIFY:
        shopifyModal.onFalse();
        break;
      case Integrations.LOJA_INTEGRADA:
        lojaIntegradaModal.onFalse();
        break;
      case Integrations.YAMPI:
        yampiModal.onFalse();
        break;
      case Integrations.TRINKS:
        trinksModal.onFalse();
        break;
      default:
        break;
    }

    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.delete('modal');

    if (modalName === Integrations.SHOPIFY) {
      newSearchParams.delete('code');
      newSearchParams.delete('shop');
      newSearchParams.delete('state');
      newSearchParams.delete('hmac');
      newSearchParams.delete('timestamp');
    }

    setSearchParams(newSearchParams);
  };

  const cardIntegration = useMemo(() => watched?.cardIntegration, [watched?.cardIntegration]);

  useEffect(() => {
    if (modulePermissionsLoading) return;

    const modal = searchParams.get('modal');
    if (!modal) {
      return;
    }

    const clearModalParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('modal');
      setSearchParams(next);
    };

    const tryOpen = (cardType: IntegrationCard, hasData: boolean, open: () => void) => {
      if (!canOpenNewIntegration(cardType, hasData, modulePermissions, connectedFlags)) {
        showPremiumFeatureDialog();
        clearModalParam();
        return;
      }
      open();
    };

    switch (modal) {
      case 'tray':
        tryOpen(IntegrationCard.E_COMMERCES, Boolean(currentDataTray), trayModal.onTrue);
        break;
      case 'omie':
        tryOpen(IntegrationCard.ERP, Boolean(currentDataOmie), omieModal.onTrue);
        break;
      case 'whatsapp':
        tryOpen(IntegrationCard.COMMUNICATION, Boolean(currentDataWhatsapp), whatsappModal.onTrue);
        break;
      case 'helena':
      case 'zekicrm':
        tryOpen(IntegrationCard.COMMUNICATION, Boolean(currentDataHelena), helenaModal.onTrue);
        break;
      case 'zapi':
      case 'zekimessage':
      case 'evolutionapi':
        tryOpen(
          IntegrationCard.COMMUNICATION,
          isEvolution ? Boolean(currentDataEvolutionApi) : Boolean(currentDataZapi),
          zekiMessageModal.onTrue
        );
        break;
      case 'smsinfobip':
        tryOpen(
          IntegrationCard.COMMUNICATION,
          Boolean(currentDataSMSInfobip),
          smsInfobipModal.onTrue
        );
        break;
      case 'hostgator':
        tryOpen(IntegrationCard.E_COMMERCES, Boolean(currentDataHostgator), hostgatorModal.onTrue);
        break;
      case 'nuvemshop':
        tryOpen(IntegrationCard.E_COMMERCES, Boolean(currentDataNuvemshop), nuvemshopModal.onTrue);
        break;
      case 'shopify':
        tryOpen(IntegrationCard.E_COMMERCES, Boolean(currentDataShopify), shopifyModal.onTrue);
        break;
      case 'lojaintegrada':
        tryOpen(
          IntegrationCard.E_COMMERCES,
          Boolean(currentDataLojaIntegrada),
          lojaIntegradaModal.onTrue
        );
        break;
      case 'trinks':
        tryOpen(IntegrationCard.ERP, Boolean(currentDataTrinks), trinksModal.onTrue);
        break;
      default:
        break;
    }
  }, [
    searchParams,
    setSearchParams,
    modulePermissions,
    modulePermissionsLoading,
    connectedFlags,
    showPremiumFeatureDialog,
    currentDataTray,
    currentDataOmie,
    currentDataWhatsapp,
    currentDataHelena,
    currentDataEvolutionApi,
    currentDataZapi,
    currentDataSMSInfobip,
    currentDataHostgator,
    currentDataNuvemshop,
    currentDataShopify,
    currentDataLojaIntegrada,
    currentDataTrinks,
    isEvolution,
    trayModal.onTrue,
    omieModal.onTrue,
    whatsappModal.onTrue,
    helenaModal.onTrue,
    zekiMessageModal.onTrue,
    smsInfobipModal.onTrue,
    hostgatorModal.onTrue,
    nuvemshopModal.onTrue,
    shopifyModal.onTrue,
    lojaIntegradaModal.onTrue,
    trinksModal.onTrue,
  ]);

  useEffect(() => {
    if (modulePermissionsLoading) return;

    const code = searchParams.get('code');
    const shop = searchParams.get('shop');
    const integration = searchParams.get('integration');
    const state = searchParams.get('state');

    const clearOAuthParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('code');
      next.delete('integration');
      next.delete('shop');
      next.delete('state');
      next.delete('hmac');
      next.delete('timestamp');
      setSearchParams(next);
    };

    if (state?.startsWith('yampi_') && code) {
      if (
        !canOpenNewIntegration(
          IntegrationCard.E_COMMERCES,
          Boolean(currentDataYampi),
          modulePermissions,
          connectedFlags
        )
      ) {
        showPremiumFeatureDialog();
        clearOAuthParams();
        return;
      }

      const merchantAlias = searchParams.get('merchant') ?? '';
      const codeVerifier = sessionStorage.getItem('yampi_code_verifier') ?? '';
      clearOAuthParams();

      const connectYampi = async () => {
        try {
          const response = await setYampiIntegration({
            variables: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- code/codeVerifier added to backend input pending schema update
              input: { merchantAlias, activated: true, code, codeVerifier } as any,
            },
          });
          if (response.data?.setYampiStoreIntegration?.ok) {
            enqueueSnackbar('Yampi conectado com sucesso!', { variant: 'success' });
            sessionStorage.removeItem('yampi_code_verifier');
            sessionStorage.removeItem('yampi_state');
            await refetch();
          }
        } catch {
          enqueueSnackbar(
            'Não foi possível conectar a Yampi. Verifique as credenciais e tente novamente.',
            {
              variant: 'error',
            }
          );
        }
      };

      connectYampi();
      return;
    }

    if (code && shop) {
      const storedState = sessionStorage.getItem('shopify_oauth_state');
      const returnedState = searchParams.get('state');

      if (storedState && storedState !== returnedState) {
        enqueueSnackbar('Estado OAuth inválido. Tente novamente.', { variant: 'error' });
        clearOAuthParams();
        return;
      }

      sessionStorage.removeItem('shopify_oauth_state');

      if (
        !canOpenNewIntegration(
          IntegrationCard.E_COMMERCES,
          Boolean(currentDataShopify),
          modulePermissions,
          connectedFlags
        )
      ) {
        showPremiumFeatureDialog();
        clearOAuthParams();
        return;
      }

      shopifyModal.onTrue();
      return;
    }

    if (integration && integration === 'nuvemshop' && code) {
      if (
        !canOpenNewIntegration(
          IntegrationCard.E_COMMERCES,
          Boolean(currentDataNuvemshop),
          modulePermissions,
          connectedFlags
        )
      ) {
        showPremiumFeatureDialog();
        clearOAuthParams();
        return;
      }
      nuvemshopModal.onTrue();
      return;
    }

    if (code) {
      if (
        !canOpenNewIntegration(
          IntegrationCard.ERP,
          Boolean(currentDataBling),
          modulePermissions,
          connectedFlags
        )
      ) {
        showPremiumFeatureDialog();
        clearOAuthParams();
        return;
      }
      blingModal.onTrue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useBoolean().onTrue is stable; full modal objects retrigger every render
  }, [
    searchParams,
    setSearchParams,
    modulePermissions,
    modulePermissionsLoading,
    connectedFlags,
    showPremiumFeatureDialog,
    enqueueSnackbar,
    currentDataNuvemshop,
    currentDataBling,
    currentDataShopify,
    nuvemshopModal.onTrue,
    blingModal.onTrue,
    currentDataYampi,
    setYampiIntegration,
    refetch,
    shopifyModal.onTrue,
  ]);

  useEffect(() => {
    if (!cardIntegration || cardIntegration.length === 0) {
      setFilteredCards(allDataCardIntegrations);

      return;
    }

    const currentCards: TCardIntegration[] = [];

    cardIntegration?.forEach((cardTypeSelected: any) => {
      const matchingCards = allDataCardIntegrations.filter(
        (card) => card.type === cardTypeSelected.value
      );

      currentCards.push(...matchingCards);
    });

    setFilteredCards(currentCards);
  }, [cardIntegration, allDataCardIntegrations]);

  return (
    <FormProvider methods={methods}>
      <ConfigIntegrationsToolbar />
      <Box
        gap={3}
        display="grid"
        mt={3}
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
      >
        {filteredCards?.map((card) => <CardIntegration key={`${card?.heading}`} {...card} />)}
      </Box>

      {/* // ---------------- Modais --------------- */}
      <ConfigTrayModal
        open={trayModal.value}
        onClose={() => handleCloseModal(Integrations.TRAY)}
        currentData={currentDataTray}
        disableForm={Boolean(currentDataTray)}
        refetch={refetch}
      />
      <ConfigOmieModal
        open={omieModal.value}
        onClose={() => handleCloseModal(Integrations.OMIE)}
        currentData={currentDataOmie}
        disableForm={Boolean(currentDataOmie)}
        refetch={refetch}
      />
      <ConfigWhatsappModal
        open={whatsappModal.value}
        onClose={() => handleCloseModal(Integrations.WHATSAPP)}
        // @ts-ignore
        currentData={currentDataWhatsapp}
        disableForm={Boolean(currentDataWhatsapp)}
        refetch={refetch}
        hasData={Boolean(currentDataWhatsapp)}
      />
      <ConfigHelenaModal
        open={helenaModal.value}
        onClose={() => handleCloseModal(Integrations.HELENA)}
        currentData={currentDataHelena}
        refetch={refetchCommunicationIntegrations}
      />
      <ConfigZekiMessageModal
        open={zekiMessageModal.value}
        onClose={() => handleCloseModal(Integrations.ZAPI)}
        zekiMessageType={zekiMessage as any}
        currentDataZapi={currentDataZapi as any}
        currentDataEvolution={currentDataEvolutionApi as any}
        refetch={refetch}
      />
      <ConfigSMSInfobipModal
        open={smsInfobipModal.value}
        onClose={() => handleCloseModal(Integrations.SMS)}
        currentData={currentDataSMSInfobip}
        refetch={refetch}
      />
      <ConfigBlingModal
        open={blingModal.value}
        onClose={() => handleCloseModal(Integrations.BLING)}
        currentData={currentDataSMSInfobip}
        code={searchParams.get('code') as string | null}
        refetch={refetch}
      />

      <ConfigDapicModal
        open={dapicModal.value}
        onClose={() => handleCloseModal(Integrations.DAPIC)}
        currentData={currentDataDapic}
        refetch={refetch}
      />
      <EmailProviderModal
        open={emailProviderModal.value}
        onClose={() => handleCloseModal(Integrations.EMAIL)}
        currentData={currentDataEmailProvider}
        refetch={refetch}
      />
      <ConfigHostgatorModal
        open={hostgatorModal.value}
        onClose={() => handleCloseModal(Integrations.HOSTGATOR)}
        currentData={currentDataHostgator}
        refetch={refetch}
      />
      <ConfigNuvemshopModal
        open={nuvemshopModal.value}
        onClose={() => handleCloseModal(Integrations.NUVEMSHOP)}
        currentData={currentDataNuvemshop}
        refetch={refetch}
        code={searchParams.get('code') as string | null}
        integration={searchParams.get('integration') as string | null}
      />
      <ConfigShopifyModal
        open={shopifyModal.value}
        onClose={() => handleCloseModal(Integrations.SHOPIFY)}
        currentData={currentDataShopify}
        refetch={refetch}
        code={searchParams.get('code')}
        shop={searchParams.get('shop')}
      />
      <ConfigLojaIntegradaModal
        open={lojaIntegradaModal.value}
        onClose={() => handleCloseModal(Integrations.LOJA_INTEGRADA)}
        currentData={currentDataLojaIntegrada}
        disableForm={Boolean(currentDataLojaIntegrada)}
        refetch={refetch}
      />
      <ConfigYampiModal
        open={yampiModal.value}
        onClose={() => handleCloseModal(Integrations.YAMPI)}
        isConnected={integratedYampi}
        merchantAlias={currentDataYampi?.merchantAlias}
      />
      <ConfigTrinksModal
        open={trinksModal.value}
        onClose={() => handleCloseModal(Integrations.TRINKS)}
        currentData={currentDataTrinks}
        disableForm={Boolean(currentDataTrinks)}
        refetch={refetch}
      />
      <PremiumFeatureDialog
        open={premiumFeatureDialog.value}
        onClose={premiumFeatureDialog.onFalse}
      />
    </FormProvider>
  );
}
